import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDiscordSDK } from '../hooks/useDiscordSDK';

interface VariantStatus {
  id: string;
  status: 'pending' | 'generating' | 'compiling' | 'completed' | 'failed';
  gameId?: string;
  error?: string;
}

interface GenerationStatus {
  id: string;
  prompt: string;
  status: 'pending' | 'generating' | 'compiling' | 'completed' | 'failed';
  progress: number;
  variants: VariantStatus[];
  error?: string;
}

function Generate() {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const { user, serverId, channelId, isReady, error } = useDiscordSDK();
  const navigate = useNavigate();

  // Show loading state while SDK initializes
  if (!isReady && !error) {
    return (
      <div className="generate-page">
        <div className="loading">
          <div className="loading-spinner" />
          <p style={{ marginTop: '1rem' }}>Connecting to Discord...</p>
        </div>
      </div>
    );
  }

  // Show error if SDK failed
  if (error) {
    return (
      <div className="generate-page">
        <div style={{ color: 'var(--accent)', textAlign: 'center' }}>
          <h2>Connection Error</h2>
          <p style={{ marginTop: '1rem' }}>{error.message}</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isGenerating || !user?.id) return;

    setIsGenerating(true);
    setStatus({
      id: '',
      prompt: prompt.trim(),
      status: 'pending',
      progress: 0,
      variants: Array.from({ length: 4 }, (_, i) => ({
        id: `placeholder-${i}`,
        status: 'pending',
      })),
    });

    try {
      // Start generation
      const response = await fetch('/.proxy/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: prompt.trim(),
          creatorId: user?.id,
          serverId,
          channelId,
          variants: 4,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start generation');
      }

      const { jobId } = await response.json();

      // Poll for status
      const pollStatus = async () => {
        const statusResponse = await fetch(`/.proxy/api/generate/${jobId}`);
        if (statusResponse.ok) {
          const statusData: GenerationStatus = await statusResponse.json();
          setStatus(statusData);

          if (statusData.status === 'completed') {
            setIsGenerating(false);
            // Don't auto-navigate, let user pick a variant
          } else if (statusData.status === 'failed') {
            setIsGenerating(false);
          } else {
            // Continue polling
            setTimeout(pollStatus, 2000);
          }
        }
      };

      pollStatus();
    } catch (error) {
      console.error('Generation failed:', error);
      setIsGenerating(false);
      setStatus(prev => prev ? { ...prev, status: 'failed', error: 'Generation failed' } : null);
    }
  };

  const getStatusIcon = (s: string) => {
    switch (s) {
      case 'completed': return '✅';
      case 'generating':
      case 'compiling': return '⏳';
      case 'failed': return '❌';
      default: return '⬜';
    }
  };

  return (
    <div className="generate-page">
      <h2 style={{ marginBottom: '1rem' }}>Generate a New Game</h2>

      <form className="generate-form" onSubmit={handleSubmit}>
        <textarea
          className="input"
          placeholder="Describe your game... (e.g., 'A space shooter where you defend earth from asteroids')"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          disabled={isGenerating}
          style={{ resize: 'vertical' }}
        />
        <button
          type="submit"
          className="btn"
          disabled={!prompt.trim() || isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Generate 4 Variants'}
        </button>
      </form>

      {status && (
        <div className="generate-status">
          <h3 style={{ marginBottom: '0.5rem' }}>
            Generation Status: {status.status}
            {status.progress > 0 && status.progress < 100 && ` (${status.progress}%)`}
          </h3>
          {status.error && (
            <p style={{ color: 'var(--accent)' }}>{status.error}</p>
          )}
          <div className="progress-bar" style={{ marginBottom: '1rem' }}>
            <div
              className="progress-fill"
              style={{ width: `${status.progress}%` }}
            />
          </div>
          <div>
            {status.variants.map((variant, index) => (
              <div key={variant.id} className="variant-status">
                <span className="variant-status-icon">
                  {getStatusIcon(variant.status)}
                </span>
                <span>Variant {index + 1}: {variant.status}</span>
                {variant.gameId && (
                  <button
                    className="nav-btn"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => navigate(`/play/${variant.gameId}`)}
                  >
                    Play
                  </button>
                )}
                {variant.error && (
                  <span style={{ color: 'var(--accent)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                    {variant.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: '2rem', color: 'var(--text-secondary)' }}>
        <h4>Tips for good prompts:</h4>
        <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
          <li>Be specific about game mechanics</li>
          <li>Mention the game genre (arcade, puzzle, action)</li>
          <li>Describe how scoring should work</li>
          <li>Keep it simple - remember it's a 160x160 pixel game!</li>
        </ul>
      </div>
    </div>
  );
}

export default Generate;
